import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';

import type { RoutedDestination } from '~/modules/enquiry/domain/enquiry-outcome';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { ENQUIRY_SUBMIT_API_ROUTE } from '~/routes/remix-api.enquiry.submit';
import {
    BOOKING_AVAILABILITY_API_ROUTE,
    type BookingAvailabilityApiResponse,
} from '~/routes/remix-api.booking.availability';
import { BOOKING_BOOK_API_ROUTE } from '~/routes/remix-api.booking.book';
import { BOOKING_REQUEST_API_ROUTE } from '~/routes/remix-api.booking.request';
import type { BookingActionData } from './use-booking-schedule';
import {
    buildMeetingActionFields,
    findOfferedSlot,
    readBookingEnquiryResult,
    resolveMeetingFollowUp,
    type BookingContact,
    type BookingSlot,
    type MeetingFollowUp,
} from '../domain/book-meeting-flow';

/** Everything one send needs, captured when the reader presses the button. */
export interface BookMeetingSendPlan {
    slot: BookingSlot;
    contact: BookingContact;
    /** The enquiry submit fields, or null when the booking needs no enquiry. */
    enquiryFields: Record<string, string> | null;
    /** An enquiry this booking already created, which is never created again. */
    enquiryId: number | null;
    routed: RoutedDestination | null;
    professionalUrlSegment: string;
    siteTreeId: number;
    itemTitle?: string;
    /** The Book button's `Source`, sent with the booking or request. */
    ctaSource?: string;
}

export interface BookMeetingSubmissionHandlers {
    /** The enquiry exists; persist it so no resume ever creates it twice. */
    onEnquiryCreated: (result: {
        enquiryId: number | null;
        routed: RoutedDestination | null;
        generatedMember: boolean;
    }) => void;
    /** A routing rule turned the answers down. */
    onDeclined: (message: string) => void;
    /**
     * The reader has to sign in first: the email belongs to an account
     * (`publicUser` set), or the session is gone or names someone else.
     */
    onAuthRequired: (publicUser?: PublicUser) => void;
    /** The routed session does not offer the chosen time; choose again from it. */
    onRepick: (sessionId: number) => void;
    /** The professional cannot take bookings this way at all. */
    onUnavailable: (code: 'sessionDisabled') => void;
    onFailed: (message: string) => void;
    onSent: (result: { booked: boolean }) => void;
}

type Phase = 'idle' | 'enquiry' | 'checking' | 'action';

const toFormData = (fields: Record<string, string>) => {
    const form = new FormData();
    for (const [key, value] of Object.entries(fields)) form.set(key, value);
    return form;
};

const SEND_FAILED = 'We could not send your request just now.';

const CHECK_FAILED =
    'We could not confirm that time just now. Please try again.';

/**
 * Runs a booking to the end: the enquiry when one is needed, then the booking
 * against the session the server routed it to (checking the chosen time
 * still stands there first), or the request.
 *
 * Each leg is its own fetcher so the root loader revalidates behind it — an
 * account the enquiry made for a guest is signed in by the time the booking
 * goes out.
 */
export function useBookMeetingSubmission(
    handlers: BookMeetingSubmissionHandlers,
) {
    const enquiryFetcher = useFetcher<unknown>();
    const checkFetcher = useFetcher<BookingAvailabilityApiResponse>();
    const actionFetcher = useFetcher<BookingActionData>();
    const [phase, setPhase] = useState<Phase>('idle');
    const planRef = useRef<BookMeetingSendPlan | null>(null);
    const enquiryIdRef = useRef<number | null>(null);
    const followRef = useRef<MeetingFollowUp | null>(null);
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;

    const act = useCallback(
        (
            follow: Exclude<MeetingFollowUp, { kind: 'revalidate' }>,
            organizerMemberId?: number | null,
        ) => {
            const plan = planRef.current;
            if (!plan) return;
            followRef.current = follow;
            setPhase('action');
            void actionFetcher.submit(
                toFormData(
                    buildMeetingActionFields({
                        follow,
                        slot: plan.slot,
                        contact: plan.contact,
                        enquiryId: enquiryIdRef.current,
                        professionalUrlSegment: plan.professionalUrlSegment,
                        siteTreeId: plan.siteTreeId,
                        itemTitle: plan.itemTitle,
                        organizerMemberId,
                        ctaSource: plan.ctaSource,
                    }),
                ),
                {
                    method: 'post',
                    action:
                        follow.kind === 'book'
                            ? BOOKING_BOOK_API_ROUTE
                            : BOOKING_REQUEST_API_ROUTE,
                },
            );
        },
        [actionFetcher],
    );

    const proceed = useCallback(
        (routed: RoutedDestination | null) => {
            const plan = planRef.current;
            if (!plan) return;
            const follow = resolveMeetingFollowUp({ routed, slot: plan.slot });
            if (follow.kind !== 'revalidate') {
                act(follow);
                return;
            }
            followRef.current = follow;
            setPhase('checking');
            const params = new URLSearchParams({
                sessionId: String(follow.sessionId),
                timezone: plan.slot.timezoneName,
                singleTimezone: '1',
            });
            void checkFetcher.load(
                `${BOOKING_AVAILABILITY_API_ROUTE}?${params.toString()}`,
            );
        },
        [act, checkFetcher],
    );

    const send = useCallback(
        (plan: BookMeetingSendPlan) => {
            planRef.current = plan;
            enquiryIdRef.current = plan.enquiryId;
            if (plan.enquiryId === null && plan.enquiryFields) {
                setPhase('enquiry');
                void enquiryFetcher.submit(toFormData(plan.enquiryFields), {
                    method: 'POST',
                    action: ENQUIRY_SUBMIT_API_ROUTE,
                    encType: 'multipart/form-data',
                });
                return;
            }
            proceed(plan.routed);
        },
        [enquiryFetcher, proceed],
    );

    const reset = useCallback(() => {
        planRef.current = null;
        followRef.current = null;
        setPhase('idle');
    }, []);

    // Each leg answers once its fetcher returns to idle with the phase still
    // pointing at it. Reading `data` on any idle render instead re-read the
    // previous leg's answer as though it were this one's.
    const settled = (state: string, busy: { current: boolean }) => {
        if (state !== 'idle') {
            busy.current = true;
            return false;
        }
        if (!busy.current) return false;
        busy.current = false;
        return true;
    };

    const enquiryBusy = useRef(false);
    useEffect(() => {
        if (!settled(enquiryFetcher.state, enquiryBusy)) return;
        if (phase !== 'enquiry') return;
        const result = readBookingEnquiryResult(enquiryFetcher.data);
        switch (result.kind) {
            case 'declined':
                reset();
                handlersRef.current.onDeclined(result.message);
                return;
            case 'authRequired':
                reset();
                handlersRef.current.onAuthRequired(result.publicUser);
                return;
            case 'failed':
                reset();
                handlersRef.current.onFailed(
                    'We could not send your details just now. Please try again.',
                );
                return;
            case 'created':
                enquiryIdRef.current = result.enquiryId;
                handlersRef.current.onEnquiryCreated({
                    enquiryId: result.enquiryId,
                    routed: result.routed,
                    generatedMember: result.generatedMember,
                });
                proceed(result.routed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enquiryFetcher.state, enquiryFetcher.data]);

    const checkBusy = useRef(false);
    useEffect(() => {
        if (!settled(checkFetcher.state, checkBusy)) return;
        if (phase !== 'checking') return;
        const plan = planRef.current;
        const follow = followRef.current;
        if (!plan || follow?.kind !== 'revalidate') return;
        const response = checkFetcher.data;
        if (response?.error?.code === 'sessionDisabled') {
            reset();
            handlersRef.current.onUnavailable('sessionDisabled');
            return;
        }
        // Only an answered check can say the time is gone. A failed or refused
        // one says nothing about the slot, and sending the reader back to pick
        // again would loop on an error another pick cannot fix.
        if (!response?.availability || response.error) {
            reset();
            handlersRef.current.onFailed(CHECK_FAILED);
            return;
        }
        const offered = findOfferedSlot(response.availability, plan.slot);
        if (!offered.offered) {
            reset();
            handlersRef.current.onRepick(follow.sessionId);
            return;
        }
        act(
            { kind: 'book', sessionId: follow.sessionId },
            offered.organizerMemberId,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkFetcher.state, checkFetcher.data]);

    const actionBusy = useRef(false);
    useEffect(() => {
        if (!settled(actionFetcher.state, actionBusy)) return;
        if (phase !== 'action') return;
        const data = actionFetcher.data;
        const booked = followRef.current?.kind === 'book';
        reset();
        if (data?.ok) {
            handlersRef.current.onSent({ booked });
            return;
        }
        if (data?.needsLogin) {
            handlersRef.current.onAuthRequired();
            return;
        }
        handlersRef.current.onFailed(data?.message || SEND_FAILED);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionFetcher.state, actionFetcher.data]);

    return {
        send,
        reset,
        /** A leg is on the wire. */
        isSending: phase !== 'idle',
        phase,
    };
}
